import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { authOperations } from "../../store/authMachine";
import { getAuthLinkErrorMessage } from "./authLinkError";
import { parseAuthLinkToken } from "./authLinkToken";
import { forgetAuthLinkSpent, isAuthLinkSpent, markAuthLinkSpent } from "./spentAuthLink";

export type AuthLinkConfirmationStatus = "idle" | "confirming" | "success" | "error";

export type UseAuthLinkConfirmationResult =
  | {
      linkPresent: boolean;
      status: "idle" | "confirming" | "success";
      confirm: () => Promise<void>;
    }
  | { linkPresent: boolean; status: "error"; errorMessage: string; confirm: () => Promise<void> };

/**
 * Parses sign-in-link token material out of the current location without
 * consuming it, and exposes `confirm()` as the sole, explicit-click-gated
 * point where the token is exchanged for a session (prevents email
 * link-scanners from burning the token before the real user clicks).
 *
 * A submitted link is remembered for the tab (recorded before the exchange — see
 * confirm()), because the pages hosting the gate remount mid-flow while the spent token
 * is still on the URL: ProtectedRoute swaps in a loader while firm context loads. Without
 * that memory the gate would re-arm and ask the user to burn an already-consumed token.
 */
export const useAuthLinkConfirmation = (): UseAuthLinkConfirmationResult => {
  const location = useLocation();
  const token = useMemo(() => parseAuthLinkToken(location), [location.search, location.hash]);
  const [status, setStatus] = useState<AuthLinkConfirmationStatus>(() =>
    token !== null && isAuthLinkSpent(token) ? "success" : "idle"
  );
  const [errorMessage, setErrorMessage] = useState<string>();
  const hasStartedRef = useRef(false);

  const confirm = useCallback(async (): Promise<void> => {
    if (!token || hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;
    setStatus("confirming");
    // Recorded before the exchange, not after: a single-use token is spent the moment it
    // is submitted, and the successful case remounts the hosting page mid-await — a write
    // afterwards would land too late for the remounted gate to see it.
    markAuthLinkSpent(token);

    const { error } = await authOperations.confirmAuthLink(token);
    if (error) {
      // A rejected exchange never consumed the token, so the emailed link is still live:
      // undo the pre-write, or re-opening it in this tab would skip the gate entirely and
      // strand the user with no session. This only re-arms a later mount — hasStartedRef
      // deliberately stays latched so a settled error cannot re-fire the exchange here.
      forgetAuthLinkSpent();
      setStatus("error");
      setErrorMessage(getAuthLinkErrorMessage(error.code));
      return;
    }
    setStatus("success");
  }, [token]);

  return status === "error"
    ? { linkPresent: token !== null, status, errorMessage: errorMessage ?? "", confirm }
    : { linkPresent: token !== null, status, confirm };
};
